import { useEffect } from 'react'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { useUIStore } from '@store/uiStore'
import { useAuthStore } from '@store/authStore'
import { createAppTheme } from '@styles/theme'
import AppRoutes from './router'
import axios from '@api/axios'

function App() {
    const { language, darkMode, themeSettings, setThemeSettings } = useUIStore()
    const { token, logout } = useAuthStore()

    // Validate stored token on startup — clears stale tokens after DB resets / expiry
    useEffect(() => {
        if (!token) return
        axios.get('/auth/me').catch((err) => {
            if (err.response?.status === 401) {
                logout()
                window.location.href = '/login'
            }
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Load theme settings from API on app start (takes priority over localStorage)
    useEffect(() => {
        axios.get('/settings/theme')
            .then(res => {
                const data = res.data?.data || res.data
                if (data) {
                    // Merge API settings with current settings
                    setThemeSettings({
                        logoUrl: data.logoUrl || themeSettings.logoUrl,
                        faviconUrl: data.faviconUrl || themeSettings.faviconUrl,
                        primaryColor: data.primaryColor || themeSettings.primaryColor,
                        secondaryColor: data.secondaryColor || themeSettings.secondaryColor,
                        accentColor: data.accentColor || themeSettings.accentColor,
                        successColor: data.successColor || themeSettings.successColor,
                        warningColor: data.warningColor || themeSettings.warningColor,
                        errorColor: data.errorColor || themeSettings.errorColor,
                        fontFamily: data.fontFamily || themeSettings.fontFamily,
                        borderRadius: data.borderRadius ?? themeSettings.borderRadius,
                        systemNameEn: data.systemNameEn || themeSettings.systemNameEn,
                        systemNameAr: data.systemNameAr || themeSettings.systemNameAr,
                    })
                }
            })
            .catch(err => {
                console.log('Could not load theme settings:', err.message)
            })
    }, [])

    // Update Favicon based on themeSettings
    useEffect(() => {
        if (themeSettings.faviconUrl) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.head.appendChild(link);
            }
            link.href = themeSettings.faviconUrl;
        }
    }, [themeSettings.faviconUrl]);

    // Update browser tab title from system identity settings
    useEffect(() => {
        const lang = document.documentElement.lang || 'en'
        const name = lang === 'ar'
            ? (themeSettings.systemNameAr || 'وزارة العدل')
            : (themeSettings.systemNameEn || 'Ministry of Justice')
        document.title = name
    }, [themeSettings.systemNameEn, themeSettings.systemNameAr]);

    const theme = createAppTheme(
        language === 'ar' ? 'rtl' : 'ltr',
        darkMode,
        themeSettings
    )

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AppRoutes />
        </ThemeProvider>
    )
}

export default App
